import { IStyle, mergeStyleSets } from "@fluentui/merge-styles";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { DATA_GRID_CONFIG } from "../../SharedComponents/Constants/CustomCustomerList.constants";
import { CustomDataGrid } from "../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { handleToggleDataGridRowSelection } from "../../Utils/js-common";
import { IPartnerSubscriptionTableProps } from "../../interfaces/PartnerView.interfaces";
import { PAU_IDEAS_URL, tooltipTexts } from "../PartnerSearch/PartnerSearch.constants";

const styles = mergeStyleSets({
  featureTable: {
    height: 600,
    width: "100%"
  } as IStyle
});

export default function PartnerSubscriptionTable(props: IPartnerSubscriptionTableProps) {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [paginationOptions, setPaginationOptions] = useState(DATA_GRID_CONFIG.pagination);
  const handleSelection = handleToggleDataGridRowSelection(selectionModel, setSelectionModel);
  useEffect(() => {
    setTimeout(() => {
      const elements = document.getElementsByClassName("MuiDataGrid-virtualScroller");
      Array.from(elements).forEach(function (element) {
        element.setAttribute("tabIndex", "0");
      });
    }, 1000);
  }, []);

  const columns: GridColDef[] = [
    {
      headerName: "SubscriptionId",
      field: "subscriptionId",
      width: 250,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "MPNId",
      field: "mpnId",
      width: 100,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "MPNName",
      field: "mpnName",
      width: 150,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "TenantId",
      field: "tenantId",
      width: 260,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "TenantName",
      field: "tenantName",
      width: 200,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "SkuName",
      field: "skuName",
      width: 180,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "SkuCategory",
      field: "skuCategory",
      minWidth: 80,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "SkuAssigned",
      field: "skuAssigned",
      minWidth: 100,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "SubscriptionStatus",
      field: "subscriptionStatus",
      minWidth: 100,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "Tier1PartnerMPNId",
      field: "tier1PartnerMPNId",
      minWidth: 100,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "Tier1PartnerMPNName",
      field: "tier1PartnerMPNName",
      minWidth: 150,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "Tier2PartnerMPNId",
      field: "tier2PartnerMPNId",
      width: 150,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "Tier2PartnerMPNName",
      field: "tier2PartnerMPNName",
      width: 150,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "SubscriptionCreatedDate",
      field: "subscriptionCreatedDate",
      width: 200,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "SubscriptionEndDate",
      field: "subscriptionEndDate",
      width: 150,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "RenewalBucket",
      field: "renewalBucket",
      width: 150,
      sortable: true,
      hideSortIcons: true
    },
    {
      headerName: "TotalSeats",
      field: "totalSeats",
      width: 100,
      sortable: true,
      hideSortIcons: true,
      type: "number"
    },
    {
      headerName: "TotalPAU",
      field: "totalPAU",
      width: 130,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {params.colDef.headerName}
          <Tooltip
            title={
              <span>
                {tooltipTexts.totalPAU + " "}
                <a
                  href={PAU_IDEAS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "white", textDecoration: "underline", fontWeight: "bold" }}
                >
                  You can read all about PAU here.
                </a>
              </span>
            }
            style={{ alignItems: "center", marginBottom: 1 }}
          >
            <InfoOutlineIcon
              aria-label="Info"
              sx={{
                fontSize: "1.3rem"
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </span>
      )
    }
  ];

  return (
    <div className={styles.featureTable}>
      <CustomDataGrid
        enableUrlParams
        rows={{ values: props.tableData ?? [], count: props.tableData?.length ?? 0 }}
        columns={columns}
        getRowId={(row) =>
          row.subscriptionId + row.subscriptionCreatedDate + row.subscriptionEndDate + row.skuCategory + row.skuAssigned
        }
        onRowSelectionModelChange={handleSelection}
        rowSelectionModel={selectionModel}
        paginationOptions={paginationOptions}
        setPaginationOptions={setPaginationOptions}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 }
          }
        }}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            pointerEvents: "none" // disables all pointer events for header
          },
          "& .MuiDataGrid-columnHeader .MuiSvgIcon-root": {
            pointerEvents: "auto", // re-enables pointer events for icons
            cursor: "pointer" // shows pointer cursor on hover
          },
          "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
            pointerEvents: "auto", // re-enables pointer events for menu icon
            cursor: "pointer" // shows pointer cursor on hover
          }
        }}
        kustoLink={props.kustoLink}
      />
    </div>
  );
}
